import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePortalScope } from "@/contexts/portalScope";

interface MarketplaceRouteProps {
  children: ReactNode;
}

/**
 * Guards routes that only exist on the open marketplace. Affiliates scoped to
 * a single white-label brand are sent back to their active brands instead of
 * seeing a page their portal does not have.
 *
 * Renders nothing until the scope resolves — redirecting on the loading
 * default would bounce ordinary affiliates off a page they are entitled to.
 */
export function MarketplaceRoute({ children }: MarketplaceRouteProps) {
  const { isLoading, isMarketplaceHidden } = usePortalScope();

  if (isLoading) return null;
  if (isMarketplaceHidden) return <Navigate to="/affiliate/brands" replace />;

  return <>{children}</>;
}

/**
 * Guards routes that render *other brands* — the cross-brand rewards
 * leaderboard, for now. Same wait-then-decide shape as MarketplaceRoute; a
 * scoped affiliate lands back on their dashboard.
 */
export function CrossBrandRoute({ children }: MarketplaceRouteProps) {
  const { isLoading, isConfined } = usePortalScope();

  // A portal host confines immediately; the scope source waits for its fetch.
  if (isConfined) return <Navigate to="/affiliate/dashboard" replace />;
  if (isLoading) return null;

  return <>{children}</>;
}
